import 'focus-within-polyfill';

import './global/jquery-migrate';
import './common/select-option-plugin';
import PageManager from './page-manager';
import quickSearch from './global/quick-search';
import currencySelector from './global/currency-selector';
import mobileMenuToggle from './global/mobile-menu-toggle';
import menu from './global/menu';
import foundation from './global/foundation';
import quickView from './global/quick-view';
import cartPreview from './global/cart-preview';
import privacyCookieNotification from './global/cookieNotification';
import carousel from './common/carousel';
import svgInjector from './global/svg-injector';
import productOptionGraphql from './set-product-option-with-graphql';
import customJS from './custom';
import initAddressAutocomplete from './address-autocomplete';

export default class Global extends PageManager {
    onReady() {
        const createAccountGrid = document.querySelector('.create-account-grid');

        if (createAccountGrid) {
            const getFieldWrapper = (candidates) => {
                for (const selector of candidates) {
                    const input = createAccountGrid.querySelector(selector);
                    if (input) {
                        return input.closest('.form-field');
                    }
                }
                return null;
            };

            const setFieldLayout = (candidates, order, flexBasis) => {
                const wrapper = getFieldWrapper(candidates);
                if (!wrapper) return;
                wrapper.style.order = String(order);
                wrapper.style.flex = flexBasis;
                wrapper.style.maxWidth = flexBasis.includes('100%') ? '100%' : 'none';
            };

            // Row 1
            setFieldLayout([
                '[data-field-type="FirstName"]',
                '[data-label="First Name"]',
            ], 1, '0 0 calc(50% - 8px)');

            setFieldLayout([
                '[data-field-type="LastName"]',
                '[data-label="Last Name"]',
            ], 2, '0 0 calc(50% - 8px)');

            // Row 2
            setFieldLayout([
                '[data-field-type="CompanyName"]',
                '[data-label="Company Name"]',
            ], 3, '0 0 100%');

            // Row 3
            setFieldLayout([
                '[data-field-type="EmailAddress"]',
                '[data-field-type="Email"]',
                '[data-label="Email Address"]',
                '[type="email"]',
            ], 4, '0 0 calc(50% - 8px)');

            setFieldLayout([
                '[data-field-type="PhoneNumber"]',
                '[data-field-type="Phone"]',
                '[data-label="Phone Number"]',
                '[type="tel"]',
            ], 5, '0 0 calc(50% - 8px)');

            // Row 4
            setFieldLayout([
                '[data-field-type="Password"]',
                '[data-label="Password"]',
                'input[type="password"]:not([data-label="Confirm Password"])',
            ], 6, '0 0 calc(50% - 8px)');

            setFieldLayout([
                '[data-field-type="ConfirmPassword"]',
                '[data-field-type="Confirm Password"]',
                '[data-label="Confirm Password"]',
            ], 7, '0 0 calc(50% - 8px)');

            // Row 5
            const addressLookup = createAccountGrid.querySelector('.create-account-address-lookup');
            if (addressLookup) {
                addressLookup.style.order = '8';
                addressLookup.style.flex = '0 0 100%';
                addressLookup.style.maxWidth = '100%';
            }

            // Row 6
            setFieldLayout([
                '[data-field-type="AddressLine1"]',
                '[data-label="Address Line 1"]',
            ], 9, '0 0 calc(50% - 8px)');

            setFieldLayout([
                '[data-field-type="AddressLine2"]',
                '[data-label="Address Line 2"]',
            ], 10, '0 0 calc(50% - 8px)');

            // Row 7
            setFieldLayout([
                '[data-field-type="City"]',
                '[data-label="Suburb/City"]',
                '[data-label="City"]',
            ], 11, '0 0 calc(33.333% - 11px)');

            setFieldLayout([
                'select[data-field-type="State"]',
                'select[data-field-type="StateProvince"]',
                '[data-label="State/Province"]',
            ], 12, '0 0 calc(33.333% - 11px)');

            setFieldLayout([
                '[data-field-type="Zip"]',
                '[data-field-type="PostalCode"]',
                '[data-label="Zip/Postcode"]',
                '[data-label="Zip Code"]',
            ], 13, '0 0 calc(33.333% - 11px)');

            // Hide country and default it
            const countryWrapper = getFieldWrapper([
                'select[data-field-type="Country"]',
                '[data-field-type="Country"]',
                '[data-label="Country"]',
            ]);

            const countryInput = countryWrapper ? countryWrapper.querySelector('select, input') : null;

            if (countryWrapper) {
                countryWrapper.style.display = 'none';
            }

            if (countryInput) {
                const tag = countryInput.tagName.toLowerCase();

                if (tag === 'select') {
                    const options = Array.from(countryInput.options);
                    const usOption =
                        options.find((opt) => opt.value === 'United States') ||
                        options.find((opt) => opt.value === 'US') ||
                        options.find((opt) => opt.value === 'USA') ||
                        options.find((opt) => opt.text.trim().toLowerCase() === 'united states') ||
                        options.find((opt) => opt.text.trim().toLowerCase() === 'united states of america');

                    if (usOption) {
                        countryInput.value = usOption.value;
                        countryInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                } else {
                    countryInput.value = 'United States';
                    countryInput.dispatchEvent(new Event('input', { bubbles: true }));
                    countryInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        }
        const setTabOrder = (selectors, tabIndex) => {
            for (const selector of selectors) {
                const field = document.querySelector(selector);
                if (field) {
                    field.setAttribute('tabindex', String(tabIndex));
                    return;
                }
            }
        };

        setTabOrder([
            '[data-field-type="FirstName"]',
            '[data-label="First Name"]',
        ], 1);

        setTabOrder([
            '[data-field-type="LastName"]',
            '[data-label="Last Name"]',
        ], 2);

        setTabOrder([
            '[data-field-type="CompanyName"]',
            '[data-label="Company Name"]',
        ], 3);

        setTabOrder([
            '[data-field-type="EmailAddress"]',
            '[data-field-type="Email"]',
            '[data-label="Email Address"]',
            'input[type="email"]',
        ], 4);

        setTabOrder([
            '[data-field-type="PhoneNumber"]',
            '[data-field-type="Phone"]',
            '[data-label="Phone Number"]',
            'input[type="tel"]',
        ], 5);

        setTabOrder([
            '[data-field-type="Password"]',
            '[data-label="Password"]',
            'input[type="password"]:not([data-label="Confirm Password"])',
        ], 6);

        setTabOrder([
            '[data-field-type="ConfirmPassword"]',
            '[data-field-type="Confirm Password"]',
            '[data-label="Confirm Password"]',
        ], 7);

        setTabOrder([
            '[data-address-search]',
        ], 8);

        setTabOrder([
            '[data-field-type="AddressLine1"]',
            '[data-label="Address Line 1"]',
        ], 9);

        setTabOrder([
            '[data-field-type="AddressLine2"]',
            '[data-label="Address Line 2"]',
        ], 10);

        setTabOrder([
            '[data-field-type="City"]',
            '[data-label="Suburb/City"]',
            '[data-label="City"]',
        ], 11);

        setTabOrder([
            'select[data-field-type="State"]',
            'select[data-field-type="StateProvince"]',
            '[data-field-type="State"]',
            '[data-field-type="StateProvince"]',
            '[data-label="State/Province"]',
        ], 12);

        setTabOrder([
            '[data-field-type="Zip"]',
            '[data-field-type="PostalCode"]',
            '[data-label="Zip/Postcode"]',
            '[data-label="Zip Code"]',
        ], 13);

        setTabOrder([
            '.create-account-submit',
            'input[type="submit"]',
        ], 14);
        
        const createAccountForm = document.querySelector('[data-create-account-form]');

        if (createAccountForm) {
            console.log('create-account autocomplete init');

            initAddressAutocomplete({
                search: '[data-address-search]',
                address1: '[data-field-type="AddressLine1"]',
                city: '[data-field-type="City"]',
                state: 'select[data-field-type="State"], select[data-field-type="StateProvince"]',
                zip: '[data-field-type="Zip"]',
                });
        }

        const { show_card_swatch, cartId, secureBaseUrl } = this.context;

        cartPreview(secureBaseUrl, cartId);
        quickSearch();
        currencySelector(cartId);
        foundation($(document));
        quickView(this.context);
        carousel(this.context);
        menu();
        mobileMenuToggle();
        privacyCookieNotification();
        svgInjector();
        customJS(this.context);

        if (show_card_swatch) {
            productOptionGraphql(this.context);
        }
    }
}


